<template>
  <div class="circular-motion-pro-viz">
    <div class="viz-header-compact" >
       <div class="brief" v-latex="knowledgePoint"></div>
       <div class="formula-chip" v-latex>${{ activeFormula }}$</div>
    </div>

    <div class="split-layout">
      <!-- 仿真区域 -->
      <div class="simulation-stage dark-stage flex-1" style="flex: 1.2;">
        <svg width="100%" height="380" viewBox="0 0 800 600" preserveAspectRatio="xMidYMid meet">
           <defs>
              <filter id="glow-cm"><feGaussianBlur stdDeviation="3" result="blur" /><feComposite in="SourceGraphic" in2="blur" operator="over" /></filter>
              <!-- 箭头定义 -->
              <marker id="arr-v" markerWidth="12" markerHeight="10" refX="10" refY="5" orient="auto">
                 <polygon points="0 0, 12 5, 0 10" fill="#34d399" />
              </marker>
              <marker id="arr-f" markerWidth="12" markerHeight="10" refX="10" refY="5" orient="auto">
                 <polygon points="0 0, 12 5, 0 10" fill="#f43f5e" />
              </marker>
              <marker id="arr-a" markerWidth="12" markerHeight="10" refX="10" refY="5" orient="auto">
                 <polygon points="0 0, 12 5, 0 10" fill="#facc15" />
              </marker>
           </defs>

           <!-- Grid Background -->
           <g stroke="rgba(241, 245, 249, 0.03)" stroke-width="2">
              <line v-for="i in 17" :key="'v'+i" :x1="i*50" y1="0" :x2="i*50" y2="600" />
              <line v-for="i in 13" :key="'h'+i" x1="0" :y1="i*50" x2="800" :y2="i*50" />
           </g>

           <g transform="translate(400, 300)">
              <!-- 轨道圆 -->
              <circle cx="0" cy="0" :r="radius" fill="rgba(56,189,248,0.05)" stroke="#38bdf8" stroke-dasharray="10,6" stroke-width="3" />
              <circle cx="0" cy="0" r="5" fill="#f8fafc" />
              <text x="15" y="5" fill="#94a3b8" font-size="20" font-weight="900">圆心 O</text>
              
              <line 0 y1="0" :x2="pos.x" :y2="pos.y" stroke="#475569" stroke-width="2" stroke-dasharray="4,4" />
              <text :x="pos.x/2 - 15" :y="pos.y/2 - 15" fill="#cbd5e1" font-size="18" font-weight="900">r</text>

              <!-- 运动小球及矢量 -->
              <g :transform="`translate(${pos.x}, ${pos.y})`">
                 <!-- 小球 -->
                 <circle r="14" fill="#38bdf8" stroke="#fff" stroke-width="4" filter="url(#glow-cm)" />
                 
                 <!-- 线速度矢量 -->
                 <line x1="0" y1="0" :x2="vVec.x" :y2="vVec.y" stroke="#34d399" stroke-width="4" marker-end="url(#arr-v)" />
                 <text :x="vVec.x + 10" :y="vVec.y + 10" fill="#34d399" font-size="22" font-weight="900">v</text>
                 
                 <!-- 如果是探究力 -->
                 <template v-if="mode === 'force' || mode === 'both'">
                    <line x1="0" y1="0" :x2="fVec.x" :y2="fVec.y" stroke="#f43f5e" stroke-width="5" marker-end="url(#arr-f)" />
                    <text :x="fVec.x - 20" :y="fVec.y - 10" fill="#f43f5e" font-size="22" font-weight="900">F向</text>
                 </template>
                 
                 <!-- 如果是探究加速度 -->
                 <template v-if="mode === 'acceleration' || mode === 'both'">
                    <!-- 如果同时显示，偏移一下加速度 -->
                    <line x1="0" y1="0" :x2="aVec.x" :y2="aVec.y" stroke="#facc15" stroke-width="3" marker-end="url(#arr-a)" />
                    <text :x="aVec.x + 15" :y="aVec.y + 20" fill="#facc15" font-size="22" font-weight="900">a向</text>
                 </template>
              </g>

              <!-- 画一条虚线辅助表示线速度与径向始终垂直 -->
              <path :d="`M ${pos.x} ${pos.y} L ${pos.x + vVec.x} ${pos.y + vVec.y} L ${pos.x + vVec.x + fVec.x} ${pos.y + vVec.y + fVec.y}`" fill="none" stroke="transparent" />
           </g>

        </svg>
      </div>

      <!-- 右侧解析面板 -->
      <div class="simulation-stage logic-panel">
         <div class="logic-header">匀速率圆周动力学建模</div>
         <div class="flex-column-center" style="flex:1; padding: 15px;">
            
            <div class="eq-box text-center">
               <div class="eq-display text-blue">实时物理参量解算</div>
               <div class="seq-data-list">
                  <div class="data-row"><span>轨道半径 r</span><span class="hl">{{ (radius/100).toFixed(2) }} m</span></div>
                  <div class="data-row"><span>角速度 ω</span><span class="hl text-purple">{{ omega.toFixed(2) }} rad/s</span></div>
                  <div class="data-row" style="background: rgba(52,211,153,0.1)">
                     <span>线速度幅度 v = ωr</span><span class="hl text-green">{{ velocityLength.toFixed(2) }} m/s</span>
                  </div>

                  <template v-if="mode === 'acceleration' || mode === 'both'">
                     <div class="data-row" style="background: rgba(250,204,21,0.1)">
                        <span>向心加速度 a_n = v²/r</span><span class="hl text-yellow">{{ accelValue.toFixed(2) }} m/s²</span>
                     </div>
                  </template>

                  <template v-if="mode === 'force' || mode === 'both'">
                     <div class="data-row"><span>试验球质量 m</span><span class="hl">1.0 kg</span></div>
                     <div class="data-row" style="background: rgba(244,63,94,0.1)">
                        <span>向心力 F_n = mv²/r</span><span class="hl text-red">{{ forceValue.toFixed(2) }} N</span>
                     </div>
                  </template>
               </div>
            </div>

            <div class="eq-box mt-l">
               <p class="desc-text" style="font-size: 13px;"><strong>核心洞察：</strong><br/><br/>{{ tipDesc }}</p>
            </div>
         </div>
      </div>
    </div>

    <!-- 控制台 -->
    <div class="compact-controls">
      <div class="control-card">
         <div class="control-grid-mini">
            <div class="s-item" style="grid-column: span 2;">
               <label>物理侧重点</label>
               <el-radio-group v-model="mode" size="small">
                 <el-radio-button label="force">向心力特征性质</el-radio-button>
                 <el-radio-button label="acceleration">纯向心加速度运动学</el-radio-button>
                 <el-radio-button label="both">牛顿第二定律综合考量</el-radio-button>
               </el-radio-group>
            </div>
            
            <div class="s-item">
               <label>运行驱动状态 (动画)</label>
               <el-button :type="isRunning ? 'danger' : 'success'" size="small" @click="isRunning = !isRunning" style="width: 100%;">{{ isRunning ? '暂停分析' : '继续运行' }}</el-button>
            </div>

            <div class="s-item" style="grid-column: span 1;">
               <label>结构半径 r ({{ (radius/100).toFixed(2) }} m)</label>
               <el-slider v-model="radius" :min="80" :max="220" :step="10" size="small"></el-slider>
            </div>

            <div class="s-item" style="grid-column: span 2;">
               <label>旋转角速度 ω ({{ omega.toFixed(2) }} rad/s)</label>
               <el-slider v-model="omega" :min="-4" :max="4" :step="0.1" size="small"></el-slider>
            </div>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CircularMotionViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      mode: 'both',
      radius: 150,
      omega: 1.5,
      angle: 0,
      isRunning: true,
      timer: null
    }
  },
  mounted() {
    this.detectMode();
    this.startLoop();
  },
  beforeDestroy() {
     if (this.timer) cancelAnimationFrame(this.timer);
  },
  watch: {
    knowledgePoint: {
       handler() { this.detectMode(); },
       immediate: true
    }
  },
  computed: {
    topicIntro() {
       if (this.mode === 'acceleration') return '向心加速度：纯运动学视角下，速度方向疯狂被扭转的根源';
       if (this.mode === 'force') return '向心力：牛顿力学下，维持物体脱离直线的神秘侧向“拉扯”';
       return '匀速圆周运动：速度与力时刻垂直的永恒探戈';
    },
    activeFormula() {
       if (this.mode === 'acceleration') return 'a_n = v² / r = ω²r';
       if (this.mode === 'force') return 'F_n = m(v² / r) = mω²r';
       return 'F = ma';
    },
    velocityLength() { return Math.abs(this.omega) * (this.radius / 100); },
    accelValue() { return Math.pow(this.omega, 2) * (this.radius / 100); },
    forceValue() { return this.accelValue * 1.0; }, // m = 1kg
    pos() {
       return { x: Math.cos(this.angle) * this.radius, y: Math.sin(this.angle) * this.radius };
    },
    vVec() {
       // Tangent vector
       const dir = this.omega >= 0 ? 1 : -1;
       const rad = this.angle + (Math.PI / 2) * dir;
       // Scale visual arrow length
       const visLen = this.velocityLength * 30; 
       return { x: Math.cos(rad) * visLen, y: Math.sin(rad) * visLen };
    },
    fVec() {
       // Center pointing vector
       const rad = this.angle + Math.PI;
       // Limit max visual length for clean UI
       const visLen = Math.min(this.forceValue * 15, 120); 
       return { x: Math.cos(rad) * visLen, y: Math.sin(rad) * visLen };
    },
    aVec() {
       // Also center pointing, maybe slightly shifted visually if 'both'
       const rad = this.angle + Math.PI;
       let shiftAng = this.mode === 'both' ? 0.05 : 0; 
       // Scale visual arrow
       const visLen = Math.min(this.accelValue * 15 * (this.mode==='both'?0.8:1), 120); 
       return { x: Math.cos(rad + shiftAng) * visLen, y: Math.sin(rad + shiftAng) * visLen };
    },
    tipDesc() {
       if (this.mode === 'acceleration') return '很多人会误解“匀速”二字。匀速圆周的【线速度速率】确实不变，但它的【方向】每分每秒都在急剧偏转！速度是有方向的矢量，方向改变意味着必须存在加速度！这个只负责变向、不负责变速的加速度，永远精准地指向圆心。';
       if (this.mode === 'force') return '一切物体都想沿着惯性飞出直线切线！是什么拽住了它？是向心力。注意，向心力不是一种“新力”，它是绳子拉力、轨道德侧壁弹力、或者是万有引力的【代名词】。当引力恰好等于所需向心力时，它就能完美绕轨飞行。否则，离心或坠毁就在瞬间。';
       return '向心加速度是外在运动学表现（怎么动），而向心力是内在动力学机制（为什么这么动）。这是由牛顿第二定律 F=ma 搭建的一座伟大的物理学桥梁。任何时候你研究圆周运动，寻找提供那股拉力的来源，列出 F_提供 = F_需要，方程就彻底解开了！';
    }
  },
  methods: {
    detectMode() {
      const kp = this.knowledgePoint || '';
      if (/加速度/.test(kp)) {
         this.mode = 'acceleration';
      } else if (/向心力/.test(kp)) {
         this.mode = 'force';
      } else {
         this.mode = 'both';
      }
    },
    startLoop() {
       let lastTime = performance.now();
       const tick = (now) => {
          if (this.isRunning) {
             const dt = (now - lastTime) / 1000;
             this.angle += this.omega * dt;
          }
          lastTime = now;
          this.timer = requestAnimationFrame(tick);
       };
       this.timer = requestAnimationFrame(tick);
    }
  }
}
</script>

<style scoped lang="scss">
.circular-motion-pro-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow-y: auto; overflow-x: hidden; padding-right: 5px; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 15px; color: #1e293b; font-weight: 700; } .formula-chip { background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; padding: 8px 18px; border-radius: 6px; font-weight: 900; font-family: -apple-system, sans-serif; font-size: 18px; } }
.split-layout { display: flex; gap: 12px; flex: 1; min-height: 360px; }
.flex-1 { flex: 1; min-width: 0; }
.logic-panel { flex: 0.8; min-width: 0; display: flex; flex-direction: column; background: #0f172a !important; border-radius: 12px; }
.logic-header { background: #1e293b; padding: 12px; color: #38bdf8; font-size: 14px; font-weight: 900; text-align: center; border-bottom: 2px solid #0f172a; border-radius: 12px 12px 0 0; }
.flex-column-center { display: flex; flex-direction: column; justify-content: flex-start; overflow-y: auto; }

.eq-box { width: 100%; border-radius: 8px; overflow: hidden; }
.eq-display { background: rgba(0,0,0,0.4); padding: 10px; font-weight: 900; border-bottom: 1px solid rgba(255,255,255,0.05); }
.seq-data-list { display: flex; flex-direction: column; background: rgba(15, 23, 42, 0.6); }
.data-row { display: flex; justify-content: space-between; align-items: center; padding: 12px 18px; border-bottom: 1px solid rgba(255,255,255,0.02); color: #94a3b8; font-size: 13px; font-weight: bold; }
.hl { color: #f8fafc; font-weight: 900; font-family: monospace; font-size: 14px; }

.text-yellow { color: #facc15; } .text-green { color: #34d399; } .text-blue { color: #38bdf8; } .text-purple { color: #a855f7; } .text-red { color: #f43f5e; }
.desc-text { color: #cbd5e1; font-size: 13px; line-height: 1.6; padding: 18px; background: rgba(56, 189, 248, 0.05); border-radius: 8px; border: 1px solid rgba(56, 189, 248, 0.1); }
.mt-l { margin-top: 25px; }

.simulation-stage { background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }

.compact-controls { flex: 0; display: flex; flex-direction: column; gap: 10px; }
.control-card { background: #ffffff; padding: 16px 20px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-grid-mini { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 20px; .s-item { label { font-size: 13px; color: #475569; font-weight: 900; margin-bottom: 8px; display: block; } } }
</style>
